import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import "./SupplierOverview.css";
import { downloadSupplierPoPdf } from "../utils/pdfDocs";

const getLogoSrc = (logo) => {
  if (!logo) return "";
  if (typeof logo !== "string") return "";
  if (logo.startsWith("data:image")) return logo;
  if (/^https?:\/\//i.test(logo)) return logo;
  return logo.startsWith("/") ? logo : `/${logo}`;
};

const SupplierDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [pRes, dRes] = await Promise.allSettled([
          API.get("/supplier/profile"),
          API.get("/supplier/dispatches"),
        ]);
        setProfile(pRes.status === "fulfilled" ? (pRes.value?.data || null) : null);
        setDispatches(
          dRes.status === "fulfilled" && Array.isArray(dRes.value?.data)
            ? dRes.value.data
            : []
        );
      } catch (err) {
        console.error("Failed to load supplier dashboard", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setLogoFailed(false);
  }, [profile?.logo]);

  const total = dispatches.length;
  const pending = dispatches.filter((d) => d.approvalStatus === "pending").length;
  const approved = dispatches.filter((d) => d.approvalStatus === "approved").length;
  const rejected = dispatches.filter((d) => d.approvalStatus === "rejected").length;

  const company = profile?.companyName || profile?.supplierName || "Supplier";
  const certStatus = profile?.certificate?.issuedAt ? "generated" : "pending";
  const statusLabel =
    certStatus === "generated" ? "Certified Supplier" : "Verification Pending";
  const po = profile?.po || null;
  const poTotalValue =
    po?.totalValue != null
      ? po.totalValue
      : (po?.totalQty != null && po?.pricePerUnit != null ? po.totalQty * po.pricePerUnit : null);
  const poPaid = po?.paidAmount ?? 0;
  const poDue = poTotalValue != null ? Math.max(0, poTotalValue - poPaid) : null;

  const completionScore = useMemo(() => {
    const has = (v) => String(v || "").trim().length > 0;
    const checks = [
      has(profile?.companyName),
      has(profile?.gstNumber),
      has(profile?.panNumber),
      has(profile?.address),
      has(profile?.contactPerson),
      has(profile?.contactMobile),
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [profile]);

  const recent = dispatches.slice(0, 5);
  const initials = company
    .split(" ")
    .map((s) => s.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const logoUrl = getLogoSrc(profile?.logo);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="success" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="supplier-dashboard vendor-dashboard">
      <section className="vd-card vd-hero">
        <div className="vd-hero-main">
          <div className="vd-hero-left">
            <div className="vd-logo-wrap" aria-label="Supplier logo">
              {logoUrl && !logoFailed ? (
                <img
                  src={logoUrl}
                  alt="Supplier logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  loading="lazy"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <div className="vd-logo-loading">{initials}</div>
              )}
            </div>
            <div>
              <div className="vd-hero-title">
                <h2>{company}</h2>
                <span className={`vd-chip ${certStatus === "generated" ? "ok" : "warn"}`}>
                  {statusLabel}
                </span>
              </div>
              <p className="vd-hero-sub">Supplier Code: {profile?.supplierCode || "—"}</p>
              <div className="vd-hero-actions">
                <Button className="vd-btn-primary" onClick={() => navigate("/supplier/dispatch/create")}>
                  Create Dispatch
                </Button>
                <Button className="vd-btn-ghost" onClick={() => navigate("/supplier/profile")}>
                  Edit Profile
                </Button>
              </div>
            </div>
          </div>
          <div className="vd-hero-score">
            <div className="vd-score-label">Profile Score</div>
            <div className="vd-score">
              <span>{completionScore}</span>
              <small>/ 100</small>
            </div>
            <div className="vd-score-note">Based on profile completeness</div>
          </div>
        </div>
        <div className="vd-hero-meta">
          <div className="vd-meta-item">
            <span className="vd-meta-label">GST</span>
            <span className="vd-meta-value">{profile?.gstNumber || "—"}</span>
          </div>
          <div className="vd-meta-item">
            <span className="vd-meta-label">PAN</span>
            <span className="vd-meta-value">{profile?.panNumber || "—"}</span>
          </div>
          <div className="vd-meta-item">
            <span className="vd-meta-label">Contact</span>
            <span className="vd-meta-value">{profile?.contactMobile || "—"}</span>
          </div>
          <div className="vd-meta-item">
            <span className="vd-meta-label">Status</span>
            <span className="vd-meta-value">{profile?.status || "active"}</span>
          </div>
        </div>
      </section>

      <section className="vd-stats">
        <div className="vd-stat-card">
          <div>
            <div className="vd-stat-label">Total Dispatches</div>
            <div className="vd-stat-value">{total}</div>
          </div>
          <div className="vd-stat-icon">inventory_2</div>
        </div>
        <div className="vd-stat-card amber">
          <div>
            <div className="vd-stat-label">Pending</div>
            <div className="vd-stat-value">{pending}</div>
          </div>
          <div className="vd-stat-icon">hourglass_empty</div>
        </div>
        <div className="vd-stat-card green">
          <div>
            <div className="vd-stat-label">Approved</div>
            <div className="vd-stat-value">{approved}</div>
          </div>
          <div className="vd-stat-icon">task_alt</div>
        </div>
        <div className="vd-stat-card">
          <div>
            <div className="vd-stat-label">Rejected</div>
            <div className="vd-stat-value">{rejected}</div>
          </div>
          <div className="vd-stat-icon">cancel</div>
        </div>
      </section>

      <section className="vd-card">
        <div className="vd-card-header">
          <h3>Supplier Details</h3>
          <Button variant="link" className="vd-link-btn" onClick={() => navigate("/supplier/profile")}>
            Update Profile
          </Button>
        </div>
        <div className="vd-details">
          <div>
            <div className="vd-label">Company Name</div>
            <div className="vd-value">{profile?.companyName || "—"}</div>
          </div>
          <div>
            <div className="vd-label">Supplier Name</div>
            <div className="vd-value">{profile?.supplierName || "—"}</div>
          </div>
          <div>
            <div className="vd-label">GST Number</div>
            <div className="vd-value">{profile?.gstNumber || "—"}</div>
          </div>
          <div>
            <div className="vd-label">PAN Number</div>
            <div className="vd-value">{profile?.panNumber || "—"}</div>
          </div>
          <div className="vd-span-2">
            <div className="vd-label">Pickup Address</div>
            <div className="vd-value">{profile?.address || "—"}</div>
          </div>
          <div>
            <div className="vd-label">Contact Person</div>
            <div className="vd-value">{profile?.contactPerson || "—"}</div>
          </div>
          <div>
            <div className="vd-label">Contact Mobile</div>
            <div className="vd-value">{profile?.contactMobile || "—"}</div>
          </div>
          <div>
            <div className="vd-label">Certificate</div>
            <div className={`vd-status ${certStatus === "generated" ? "ok" : "warn"}`}>
              {certStatus === "generated" ? "Approved" : "Pending"}
            </div>
          </div>
        </div>
      </section>

      <section className="vd-card">
        <div className="vd-card-header">
          <h3>Purchase Order</h3>
          <div className="d-flex align-items-center gap-2">
            <span className="vd-subtle">Admin set PO summary</span>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => navigate("/supplier/po-details")}
            >
              View All PO Details
            </Button>
            {po?.totalQty != null ? (
              <Button
                size="sm"
                variant="outline-dark"
                onClick={() => downloadSupplierPoPdf({ profile, dispatches })}
              >
                Download PO PDF
              </Button>
            ) : null}
          </div>
        </div>
        {po?.totalQty != null ? (
          <div className="vd-details">
            <div>
              <div className="vd-label">PO Number</div>
              <div className="vd-value">{po.poNumber || "—"}</div>
            </div>
            <div>
              <div className="vd-label">Total Qty</div>
              <div className="vd-value">{po.totalQty} {po.unit || "ton"}</div>
            </div>
            <div>
              <div className="vd-label">Remaining Qty</div>
              <div className="vd-value">{po.remainingQty ?? "—"} {po.unit || "ton"}</div>
            </div>
            <div>
              <div className="vd-label">Rate</div>
              <div className="vd-value">₹{po.pricePerUnit ?? "—"} / {po.unit || "ton"}</div>
            </div>
            <div>
              <div className="vd-label">Total Value</div>
              <div className="vd-value">₹{poTotalValue ?? "—"}</div>
            </div>
            <div>
              <div className="vd-label">Paid</div>
              <div className="vd-value">₹{poPaid ?? "—"}</div>
            </div>
            <div>
              <div className="vd-label">Due</div>
              <div className="vd-value">₹{poDue ?? "—"}</div>
            </div>
          </div>
        ) : (
          <div className="text-muted">PO not set by admin yet.</div>
        )}
      </section>

      <section className="vd-card">
        <div className="vd-card-header">
          <h3>Recent Dispatches</h3>
          <Button variant="link" className="vd-link-btn" onClick={() => navigate("/supplier/dispatch/history")}>
            View All
          </Button>
        </div>
        <div className="table-responsive vd-desktop-table">
          <Table className="vd-table" hover>
            <thead>
              <tr>
                <th>Dispatch Date</th>
                <th>Material</th>
                <th>Qty</th>
                <th>Vehicle</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted">No dispatches yet.</td></tr>
              ) : (
                recent.map((d) => (
                  <tr key={d._id}>
                    <td>{d.dispatchDateTime ? new Date(d.dispatchDateTime).toLocaleString() : "—"}</td>
                    <td>{d.materialName || "—"}</td>
                    <td>{d.quantity || "—"} {d.unit || ""}</td>
                    <td>{d.vehicle?.number || "—"}</td>
                    <td>
                      <span className={`vd-pill ${d.approvalStatus === "approved" ? "ok" : d.approvalStatus === "pending" ? "warn" : "muted"}`}>
                        {d.approvalStatus || "draft"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        <div className="vd-mobile-list">
          {recent.length === 0 ? (
            <div className="text-center text-muted">No dispatches yet.</div>
          ) : (
            recent.map((d) => (
              <div className="vd-mobile-card" key={d._id}>
                <div className="vd-mobile-row">
                  <strong>{d.materialName || "—"}</strong>
                  <span className={`vd-pill ${d.approvalStatus === "approved" ? "ok" : d.approvalStatus === "pending" ? "warn" : "muted"}`}>
                    {d.approvalStatus || "draft"}
                  </span>
                </div>
                <div className="small text-muted">{d.dispatchDateTime ? new Date(d.dispatchDateTime).toLocaleString() : "—"}</div>
                <div className="vd-mobile-meta">
                  <span>Qty: {d.quantity || "—"} {d.unit || ""}</span>
                  <span>Vehicle: {d.vehicle?.number || "—"}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default SupplierDashboard;
